import { Package, Sparkles, Star } from "lucide-react"
import { Product } from "../../types/search"
import { cn } from "../../utils/cn"
import { formatPrice } from "../../utils/currency"

interface ProductCardProps {
	product: Product
	onClick?: (product: Product) => void
	className?: string
	hasSemanticSearch?: boolean
	onFindSimilar?: (product: Product) => void
}

/**
 * ProductCard displays a single product with image, name, price, rating, and stock status
 * Designed to be used within SearchResults grid
 */
export const ProductCard = ({ product, onClick, className, hasSemanticSearch, onFindSimilar }: ProductCardProps) => {
	const hasDiscount = product.originalPrice && product.originalPrice > product.price
	const discountPercentage = hasDiscount
		? Math.round(((product.originalPrice! - product.price) / product.originalPrice!) * 100)
		: 0

	const handleClick = () => {
		if (onClick) {
			onClick(product)
		}
	}

	const formattedPrice = formatPrice(product.price, product.currency)
	const formattedOriginalPrice = hasDiscount ? formatPrice(product.originalPrice!, product.currency) : null

	return (
		<div
			className={cn(
				"group relative flex flex-col rounded-lg border border-border bg-card overflow-hidden transition-all hover:shadow-md hover:border-primary/50 cursor-pointer",
				className,
			)}
			onClick={handleClick}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault()
					handleClick()
				}
			}}
			role="button"
			tabIndex={0}>
			{/* Product Image */}
			<div className="relative aspect-square bg-muted overflow-hidden">
				{product.image ? (
					<img
						alt={product.name}
						className="w-full h-full object-cover transition-transform group-hover:scale-105"
						loading="lazy"
						src={product.image}
					/>
				) : (
					<div className="w-full h-full flex items-center justify-center">
						<Package className="w-16 h-16 text-muted-foreground/30" />
					</div>
				)}

				{/* Badge (if present) */}
				{product.badge && (
					<div className="absolute top-2 left-2 px-2 py-1 rounded-md bg-primary text-primary-foreground text-xs font-medium">
						{product.badge}
					</div>
				)}

				{/* Stock Status Badge */}
				{!product.inStock && (
					<div className="absolute top-2 right-2 px-2 py-1 rounded-md bg-destructive text-destructive-foreground text-xs font-medium">
						Out of Stock
					</div>
				)}

				{/* Discount Badge */}
				{hasDiscount && (
					<div className="absolute bottom-2 right-2 px-2 py-1 rounded-md bg-green-500 text-white text-xs font-bold">
						-{discountPercentage}%
					</div>
				)}

				{/* Find Similar Button */}
				{hasSemanticSearch && onFindSimilar && (
					<div className="absolute inset-x-0 bottom-0 p-4 flex justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-gradient-to-t from-black/60 to-transparent pointer-events-none">
						<button
							className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-primary text-xs font-medium hover:bg-gray-50 shadow-lg transform translate-y-2 group-hover:translate-y-0 transition-all duration-200 pointer-events-auto"
							onClick={(e) => {
								e.stopPropagation()
								onFindSimilar(product)
							}}
							title="Find similar products">
							<Sparkles className="w-3.5 h-3.5 text-purple-500" />
							<span>Find Similar</span>
						</button>
					</div>
				)}
			</div>

			{/* Product Info */}
			<div className="flex flex-col gap-2 p-4 flex-1">
				{/* Category & Brand */}
				<div className="flex items-center gap-2 text-xs text-muted-foreground">
					<span className="truncate">{product.category}</span>
					{product.brand && (
						<>
							<span>•</span>
							<span className="truncate">{product.brand}</span>
						</>
					)}
				</div>

				{/* Product Name */}
				<h3 className="font-medium text-sm line-clamp-2 group-hover:text-primary transition-colors">{product.name}</h3>

				{/* Rating */}
				{product.rating !== undefined && (
					<div className="flex items-center gap-1">
						<div className="flex items-center">
							{[1, 2, 3, 4, 5].map((star) => (
								<Star
									className={cn(
										"w-3 h-3",
										star <= Math.round(product.rating!)
											? "fill-yellow-400 text-yellow-400"
											: "text-muted-foreground/30",
									)}
									key={star}
								/>
							))}
						</div>
						<span className="text-xs text-muted-foreground">
							{product.rating.toFixed(1)}
							{product.reviewCount && ` (${product.reviewCount})`}
						</span>
					</div>
				)}

				{/* Price */}
				<div className="flex items-baseline gap-2 mt-auto">
					<span className="text-lg font-bold">{formattedPrice}</span>
					{hasDiscount && <span className="text-sm text-muted-foreground line-through">{formattedOriginalPrice}</span>}
				</div>

				{/* Stock Quantity (if low) */}
				{product.inStock && product.stockQuantity !== undefined && product.stockQuantity < 10 && (
					<p className="text-xs text-orange-500 font-medium">Only {product.stockQuantity} left!</p>
				)}
			</div>
		</div>
	)
}
